import GraphicEditor from './Presentation/Component/InteractiveGraphic/InteractiveGraphic.Editor';
import InteractiveGraphicAnimations from './Presentation/Component/InteractiveGraphic/InteractiveGraphic.Animations';
import InteractiveGraphic from './Presentation/Component/InteractiveGraphic/InteractiveGraphic';
import './src/scripts/helpers/viewport-height';

// external libraries
import 'lazysizes';
import 'lazysizes/plugins/parent-fit/ls.parent-fit';

// Component modules — paths into Public/Frontend/src/scripts/ are the
// interim location used by Step 2 of the frontend migration; Step 3
// colocates each file under its owning Presentation/.../<Name>/ folder
// and rewrites the import here.
import JobsList                   from "../../../../CodeQ.KapschJobs/Resources/Private/Fusion/Integration/Molecule/JobsList/JobsList";
import CustomSelect               from "./Presentation/Atom/Select/Select";
import LocationSelect             from "./Presentation/Atom/LocationSelect/LocationSelect";
import FloatingButton             from "./Presentation/Molecule/FloatingButton/FloatingButton";
import HashMenu                   from "./Presentation/Organism/HashMenu/HashMenu";
import PageSelect                 from "./Presentation/Atom/PageSelect/PageSelect";
import PressNewsOverviewPage      from "./Presentation/Template/PressOverviewPage";
import ScrollTop                  from "./Presentation/Atom/ScrollTop/ScrollTop";
import HeroSlider                 from "./Presentation/Component/Hero/Hero.Slider";
import HeroText                   from "./Presentation/Component/Hero/Hero.Text";
import HeroVideo                  from "./Presentation/Component/Hero/Hero.Video";
import AnimatedIconList           from "./Presentation/Component/AnimatedIconList/AnimatedIconList";
import ProjectFacts               from "./Presentation/Molecule/ProjectFacts/ProjectFacts";
import TeaserSlider               from "./Presentation/Component/TeaserObject/TeaserObject.Slider";
import AwardsSlider               from "./Presentation/Component/TeaserObject/TeaserObject.Slider.Awards";
import Header                     from "./Presentation/Component/Header/Header";
import Accordion                  from "./Presentation/Component/Accordion/Accordion";
import Gallery                    from "./Presentation/Molecule/Gallery/Gallery";
import Video                      from "./Presentation/Atom/Video/Video";
import ScrollLinksHandler         from "./Presentation/Atom/ScrollLink/ScrollLink";
import SwitchLanguageNotice       from "./Presentation/Molecule/SwitchLanguageNotice/SwitchLanguageNotice";
import Modal                      from "./src/scripts/helpers/modal";
import Table                      from "./Presentation/Atom/RichText/RichText.Table";
import PressDetailsContent        from "./Presentation/Organism/PressDetailsContent/PressDetailsContent";
import Breadcrumbs                from "./Presentation/Molecule/Breadcrumbs/Breadcrumbs";
import QuickLinks                 from "./Presentation/Component/QuickLinks/QuickLinks";
import SectionCollapsible         from "./Presentation/Organism/Section/Section.Collapsible";
import Tooltip                    from "./Presentation/Atom/Tooltip/Tooltip";
import Preloader                  from "./src/scripts/helpers/preloader";
import TooltipSlider              from "./Presentation/Molecule/TooltipSlider/TooltipSlider";
import Forms                      from "./src/scripts/helpers/forms";
import CardList                   from "./Presentation/Organism/CardList/CardList";
import AnalyticsEvent             from "./src/scripts/helpers/analytics-event";
import Tabs                       from "./Presentation/Molecule/Tabs/Tabs";
import TabsMasonry                from "./Presentation/Molecule/Tabs/Tabs.Masonry";
import SocialMediaWall            from "./src/scripts/helpers/social-media-wall";
import EqsForm                    from "./src/scripts/helpers/eqs-form";
import Map                        from "../../../NodeTypes/Content/Map/Map";
import InteractiveMapWithAccordion from "../../../NodeTypes/Content/InteractiveMapWithAccordion/InteractiveMapWithAccordion.Backend";
import ReferencePage              from "../../../NodeTypes/Document/ReferencePage/ReferencePage.Backend";
import Timeline                   from "./Presentation/Organism/Timeline/Timeline";
import Footer                     from "./Presentation/Component/Footer/Footer";
import TeaserCardVideo            from "./Presentation/Component/TeaserCard/TeaserCard.Video";
import CookieDialog               from "./Presentation/Molecule/CookieDialog/CookieDialog";

// init application
class App {
	constructor() {
		Forms.init();
		QuickLinks.init();
		CustomSelect.init();
		LocationSelect.init();
		PageSelect.init();
		PressNewsOverviewPage.init();
		Header.init();
        FloatingButton.init();
		HashMenu.init();
		HeroText.init();
        AnalyticsEvent.init();
		HeroSlider.init();
		HeroVideo.init();
        TeaserCardVideo.init();
        AnimatedIconList.init();
        ProjectFacts.init();
		TeaserSlider.init();
		AwardsSlider.init();
		Accordion.init();
		Gallery.init();
		Video.init();
		JobsList.init();
		ScrollLinksHandler.init();
		ScrollTop.init();
		SwitchLanguageNotice.init();
		Modal.init();
		Table.init();
		PressDetailsContent.init();
		Breadcrumbs.init();
		SectionCollapsible.init();
		Tooltip.init();
		Preloader.init();
		TooltipSlider.init();
		CardList.init();
        Tabs.init();
        TabsMasonry.init();
        SocialMediaWall.init();
        EqsForm.init();
        Map.init();
        InteractiveMapWithAccordion.init();
        ReferencePage.init();
        Timeline.init();
        Footer.init();
        CookieDialog.init();
        InteractiveGraphicAnimations.init();
        InteractiveGraphic.init();

        // Defer the conditional-bundle probes until DOMContentLoaded so the
        // Fusion-rendered <script> siblings (youtube-player / vimeo-player /
        // alpine-components) are guaranteed to be in the DOM by the time we
        // check `script[src*="..."]`. Without this, Main.js evaluates while
        // the parser is still mid-body, the script-tag check returns empty,
        // and the lazyLoad fires even though Fusion already loaded the
        // bundle — a double-load on every match.
        const runConditionalLazyLoaders = () => {
            // Lottie always lazy-loads on DOM presence (no Fusion-conditional sibling).
            if (document.querySelectorAll('lottie-player').length) {
                this.lazyLoad('/_Resources/Static/Packages/CodeQ.Site/Scripts/lottie-player.js');
            }

            // Cache-stale safety net: load the conditional bundle only if its
            // DOM trigger is present AND Fusion didn't already emit the script.
            if (document.querySelectorAll('.youtube-player').length && !document.querySelectorAll('script[src*="youtube-player.js"]').length) {
                this.lazyLoad('/_Resources/Static/Packages/CodeQ.Site/Scripts/youtube-player.js');
            }
            if (document.querySelectorAll('.vimeo-player').length && !document.querySelectorAll('script[src*="vimeo-player.js"]').length) {
                this.lazyLoad('/_Resources/Static/Packages/CodeQ.Site/Scripts/vimeo-player.js');
            }
            if ((document.querySelectorAll('.imwa').length || document.querySelectorAll('.filtered-download-list').length || document.querySelectorAll('.filtered-reference-list').length) && !document.querySelectorAll('script[src*="alpine-components.js"]').length) {
                this.lazyLoad('/_Resources/Static/Packages/CodeQ.Site/Scripts/alpine-components.js');
            }
        };
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', runConditionalLazyLoaders, { once: true });
        } else {
            runConditionalLazyLoaders();
        }
	}

	get() {
		return this;
	}

	lazyLoad(url) {
		(function(w,d,s){
			let f=d.getElementsByTagName(s)[0],
			j=d.createElement(s);
			j.async=true;
			j.src=url;
			f.parentNode.insertBefore(j,f);
		})(window,document,'script');
	}
}

GraphicEditor.init();
window.app = new App();
